import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { CtaLink, GlossaryPageContent } from "../content/types";

function Cta({ cta, variant }: { cta: CtaLink; variant: "primary" | "quiet" }) {
  const className = variant === "primary" ? "btn btn--primary" : "btn btn--quiet";
  if (cta.external || cta.href.startsWith("#")) {
    return (
      <a className={className} href={cta.href}>
        {cta.label}
      </a>
    );
  }
  return (
    <Link className={className} to={cta.href}>
      {cta.label}
    </Link>
  );
}

/** Anchor id for a term, so a definition can be linked to directly. */
function slugify(term: string): string {
  return term
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * The glossary.
 *
 * Rendered as a description list because that is what it is, and terms are
 * sorted alphabetically at render time rather than in the content file — the
 * content file is grouped by subject, which is easier to maintain, and sorting
 * here means adding a term never requires finding its correct slot.
 */
export function GlossaryPage({ content }: { content: GlossaryPageContent }) {
  useEffect(() => {
    document.title = "Glossary — Odingard";
  }, []);

  const terms = [...content.terms].sort((a, b) =>
    a.term.localeCompare(b.term),
  );

  return (
    <main className="page pdp glossary" id="main">
      <section className="pdp__hero">
        <p className="page__eyebrow">{content.eyebrow}</p>
        <h1>{content.headline}</h1>
        <p className="page__lede">{content.lede}</p>
      </section>

      <section className="pdp__section" id="terms">
        <dl className="glossary__terms">
          {terms.map((entry) => (
            <div key={entry.term} id={slugify(entry.term)}>
              <dt>{entry.term}</dt>
              <dd>
                <p>{entry.definition}</p>
                {entry.source && (
                  <p className="glossary__source">{entry.source}</p>
                )}
                {entry.seeAlso && entry.seeAlso.length > 0 && (
                  <p className="glossary__see">
                    {entry.seeAlso.map((link) => (
                      <Link key={link.href} to={link.href}>
                        {link.label} →
                      </Link>
                    ))}
                  </p>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="pdp__close" id="close">
        <h2>{content.close.heading}</h2>
        <p className="pdp__body">{content.close.body}</p>
        <div className="pdp__ctas">
          {content.close.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>
    </main>
  );
}
